/**
 * Migrated to BaseDialog pattern (Phase 3.2)
 */
import {
  Edit as EditIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import {
  Button,
  Box, Typography, Alert, CircularProgress
} from '@mui/material';
import React from 'react';

import { BaseDialog } from '../../ui/BaseDialog';
import { AccessInfoCard } from './AccessInfoCard';
import { PermissionForm } from './PermissionForm';

interface DocumentPermissionsDialogProps {
  open: boolean;
  onClose: () => void;
  documentTitle: string;
  editMode: boolean;
  setEditMode: (editMode: boolean) => void;
  permissions: any;
  accessInfo: any;
  isLoadingAccess: boolean;
  accessError: Error | null;
  isUpdatingPermissions: boolean;
  updateError: Error | null;
  handleUpdatePermissions: () => Promise<void>;
  handleFieldChange: (field: string, value: unknown) => void;
}

export const DocumentPermissionsDialog: React.FC<DocumentPermissionsDialogProps> = ({
  open,
  onClose,
  documentTitle,
  editMode,
  setEditMode,
  permissions,
  accessInfo,
  isLoadingAccess,
  accessError,
  isUpdatingPermissions,
  updateError,
  handleUpdatePermissions,
  handleFieldChange
}) => {
  // Determine actions based on edit mode
  const dialogActions = editMode ? (
    <>
      <Button onClick={() => setEditMode(false)}>
        Cancel
      </Button>
      <Button
        onClick={handleUpdatePermissions}
        variant="contained"
        disabled={isUpdatingPermissions}
        startIcon={isUpdatingPermissions ? <CircularProgress size={16} /> : <SecurityIcon />}
      >
        {isUpdatingPermissions ? 'Saving...' : 'Update Permissions'}
      </Button>
    </>
  ) : (
    <Button onClick={onClose}>
      Close
    </Button>
  );

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SecurityIcon sx={{ mr: 1 }} />
          <Typography variant="h6" component="span">
            Document Permissions: {documentTitle}
          </Typography>
        </Box> as any
      }
      maxWidth="md"
      fullWidth
      actions={dialogActions}
    >
      {isLoadingAccess ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : accessError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {accessError.message}
        </Alert>
      ) : accessInfo ? (
        <>
          <AccessInfoCard
            accessInfo={accessInfo}
            permissions={permissions}
            sharedUsersDetails={accessInfo.shared_with_users_details}
            sharedGroupsDetails={accessInfo.shared_with_groups_details}
          />

          {updateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {updateError.message}
            </Alert>
          )}

          {!editMode && accessInfo.can_modify_permissions && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditMode(true)}
              >
                Edit Permissions
              </Button>
            </Box>
          )}

          <PermissionForm
            permissions={permissions}
            onFieldChange={handleFieldChange}
            canModifyPermissions={accessInfo.can_modify_permissions || false}
            editMode={editMode}
          />
        </>
      ) : null}
    </BaseDialog>
  );
};